/**
 * Google Ads conversion actions for the Journey funnel sales, and the
 * user-provided data the Google tag sends with each conversion.
 *
 * Each Journey funnel sale (offer_id journey_*) has its own conversion
 * action, configured on the google_ads marketing integration by its gtag
 * label and its conversion action id. The browser uses a kind's label only
 * when both are set and the event carries a journey_* offer_id; the server
 * (supabase/functions/_shared/googleAdsWebConversions.ts) uploads the same
 * sale to the same action under the same rule, and Google matches the two
 * copies on transaction_id. Every other sale (the $21 Challenge and its
 * upsell, single Journey and program purchases, web Premium) keeps today's
 * labels (purchase, subscription purchase, trial), and so does a Journey
 * funnel sale until its action is configured.
 *
 * Enhanced conversions for web: the tag gets plain values and hashes them
 * itself. https://support.google.com/google-ads/answer/13258081
 */

export type GoogleAdsConversionKind = 'journey_purchase' | 'journey_upsell' | 'journey_trial';

// Same rule as isJourneyFunnelOfferId in supabase/functions/_shared/funnelOffers.ts
// (and the CHECK constraint on journey_funnel_offers.offer_id).
const JOURNEY_FUNNEL_OFFER_ID_PATTERN = /^journey_[a-z0-9_]+$/;

export function isJourneyFunnelOfferId(raw: unknown): raw is string {
  return typeof raw === 'string' && JOURNEY_FUNNEL_OFFER_ID_PATTERN.test(raw);
}

// Keep in sync with GOOGLE_ADS_WEB_CONVERSIONS in
// supabase/functions/_shared/googleAdsWebConversions.ts.
export const GOOGLE_ADS_CONVERSION_KIND_CONFIG_KEYS: Record<
  GoogleAdsConversionKind,
  { labelKey: string; actionIdKey: string }
> = {
  journey_purchase: {
    labelKey: 'journey_purchase_conversion_label',
    actionIdKey: 'journey_purchase_conversion_action_id',
  },
  journey_upsell: {
    labelKey: 'journey_upsell_conversion_label',
    actionIdKey: 'journey_upsell_conversion_action_id',
  },
  journey_trial: {
    labelKey: 'journey_trial_conversion_label',
    actionIdKey: 'journey_trial_conversion_action_id',
  },
};

// The app's server-only Import actions. A web kind pointed at one of them
// would never match a tag copy, so it stays off.
const SERVER_ONLY_ACTION_ID_KEYS = [
  'trial_conversion_action_id',
  'subscription_purchase_conversion_action_id',
  'renewal_conversion_action_id',
];

// The old blended web "Purchase" action, Secondary since Oct 19 2026. Keep in
// sync with RETIRED_WEB_ACTION_IDS in supabase/functions/_shared/googleAdsWebConversions.ts.
export const RETIRED_WEB_ACTION_IDS: readonly string[] = ['7570196778'];

function stringOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  let trimmed = value.trim();
  if (
    trimmed.length >= 2 &&
    ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'")))
  ) {
    trimmed = trimmed.slice(1, -1).trim();
  }
  if (!trimmed) return null;
  const lowered = trimmed.toLowerCase();
  return lowered === 'null' || lowered === 'undefined' ? null : trimmed;
}

function conversionActionId(value: unknown): string | null {
  const raw = stringOrNull(value);
  if (!raw) return null;
  const resourceMatch = raw.match(/\/conversionActions\/(\d+)/i);
  if (resourceMatch?.[1]) return resourceMatch[1];
  return /^\d+$/.test(raw) ? raw : null;
}

/** The gtag label for a kind, or null while the kind is not fully configured. */
export function googleAdsKindLabel(
  config: Record<string, unknown> | null | undefined,
  kind: GoogleAdsConversionKind
): string | null {
  const values = config ?? {};
  const keys = GOOGLE_ADS_CONVERSION_KIND_CONFIG_KEYS[kind];
  const label = stringOrNull(values[keys.labelKey]);
  const actionId = conversionActionId(values[keys.actionIdKey]);
  if (!label || !actionId) return null;
  if (SERVER_ONLY_ACTION_ID_KEYS.some((key) => conversionActionId(values[key]) === actionId)) return null;
  if (RETIRED_WEB_ACTION_IDS.includes(actionId)) return null;
  return label;
}

export interface GoogleAdsUserIdentity {
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  postalCode?: string | null;
  /** ISO 3166-1 alpha-2 country code. */
  country?: string | null;
}

export type GoogleAdsUserData = {
  email?: string;
  phone_number?: string;
  address?: {
    first_name: string;
    last_name: string;
    postal_code: string;
    country: string;
  };
};

function normalizeEmail(raw: unknown): string | null {
  const value = stringOrNull(raw)?.toLowerCase();
  return value && value.includes('@') ? value : null;
}

/** E.164 ("+" and 8 to 15 digits). A bare 10-digit number counts only for US/CA. */
export function normalizeGoogleAdsPhone(raw: unknown, country?: string | null): string | null {
  const value = stringOrNull(raw)?.replace(/[\s().-]/g, '');
  if (!value) return null;
  const withPlus = value.startsWith('00') ? `+${value.slice(2)}` : value;
  if (withPlus.startsWith('+')) {
    return /^\+[1-9]\d{7,14}$/.test(withPlus) ? withPlus : null;
  }
  if (!/^\d+$/.test(withPlus)) return null;
  const code = normalizeCountry(country);
  if (withPlus.length === 11 && withPlus.startsWith('1') && (!code || code === 'US' || code === 'CA')) {
    return `+${withPlus}`;
  }
  if (withPlus.length === 10 && (code === 'US' || code === 'CA')) return `+1${withPlus}`;
  return null;
}

function normalizeCountry(raw: unknown): string | null {
  const value = stringOrNull(raw)?.toUpperCase();
  return value && /^[A-Z]{2}$/.test(value) ? value : null;
}

function normalizeName(raw: unknown): string | null {
  const value = stringOrNull(raw)?.replace(/\s+/g, ' ');
  return value || null;
}

function normalizePostalCode(raw: unknown, country: string | null): string | null {
  const value = stringOrNull(raw);
  if (!value) return null;
  if (country === 'US') {
    const match = value.match(/^(\d{5})(?:[\s-]?\d{4})?$/);
    return match ? match[1] : null;
  }
  return value;
}

/**
 * The Google tag's user_data for enhanced conversions. Email and phone when
 * known; the address only when first name, last name, postal code and country
 * are all known (Google's address match needs all four). Null when nothing
 * is known.
 */
export function buildGoogleAdsUserData(identity: GoogleAdsUserIdentity): GoogleAdsUserData | null {
  const data: GoogleAdsUserData = {};
  const email = normalizeEmail(identity.email);
  if (email) data.email = email;

  const country = normalizeCountry(identity.country);
  const phone = normalizeGoogleAdsPhone(identity.phone, country);
  if (phone) data.phone_number = phone;

  const firstName = normalizeName(identity.firstName);
  const lastName = normalizeName(identity.lastName);
  const postalCode = normalizePostalCode(identity.postalCode, country);
  if (firstName && lastName && postalCode && country) {
    data.address = { first_name: firstName, last_name: lastName, postal_code: postalCode, country };
  }

  return Object.keys(data).length > 0 ? data : null;
}
